import Swiper from "swiper";
import { prefersReducedMotion } from "@js/parts/prefersReducedMotion";
import {
  Navigation,
  Pagination,
  EffectFade,
  Autoplay,
  A11y,
} from "swiper/modules";

import "swiper/css";

// configure Swiper to use modules
Swiper.use([Navigation, Pagination, EffectFade, Autoplay, A11y]);

export default function slider(ops = {}) {
  return {
    animation: {
      stop: false,
      frameCount: 0,
      fps: 60,
      fpsInterval: null,
      startTime: null,
      now: null,
      then: null,
      elapsed: null,
    },
    current: 0,
    isPaused: false,
    isStopped: false,
    lastIndex: -1,
    progress: 0,
    progressPercentage: 0,

    scrollProgress: 0,
    disabled: false,

    swiper: null,
    observer: null,
    rafId: null,
    resizeHandler: null,
    visibilityHandler: null,

    shouldDisableAtBreakpoint() {
      const breakpoint = ops.breakpoint;
      const breakpointDirection = ops.breakpointDirection || "above";

      if (!breakpoint) return false;

      // Disable slider above breakpoint (min-width)
      if (breakpointDirection === "above") {
        return window.matchMedia(`(min-width: ${breakpoint})`).matches;
      }

      // Disable slider below breakpoint (max-width)
      if (breakpointDirection === "below") {
        const breakpointValue = breakpoint.match(/\d+/)?.[0];
        const breakpointUnit = breakpoint.replace(/\d+/, "");
        if (breakpointValue && breakpointUnit) {
          const maxWidth = `${parseInt(breakpointValue) - 1}${breakpointUnit}`;
          return window.matchMedia(`(max-width: ${maxWidth})`).matches;
        }
      }

      return false;
    },

    applySwiperClasses(el) {
      // Add swiper class to container with data-swiper attribute
      if (el.hasAttribute('data-swiper') && !el.classList.contains('swiper')) {
        el.classList.add('swiper');
      }

      // Add swiper-wrapper class to wrapper with data-swiper-wrapper attribute
      const wrapper = el.querySelector('[data-swiper-wrapper]');
      if (wrapper && !wrapper.classList.contains('swiper-wrapper')) {
        wrapper.classList.add('swiper-wrapper');
      }

      // Add swiper-slide class to slides with data-swiper-slide attribute
      const slides = el.querySelectorAll('[data-swiper-slide]');
      slides.forEach((slide) => {
        if (!slide.classList.contains('swiper-slide')) {
          slide.classList.add('swiper-slide');
        }
      });
    },

    removeSwiperClasses(el) {
      if (!el) return;

      if (el.hasAttribute('data-swiper')) {
        el.classList.remove('swiper');
      }

      const wrapper = el.querySelector('[data-swiper-wrapper]');
      if (wrapper) {
        wrapper.classList.remove('swiper-wrapper');
      }

      el.querySelectorAll('[data-swiper-slide]').forEach((slide) => {
        slide.classList.remove('swiper-slide');
      });
    },

    getTargetEl() {
      return ops.target ? this.$el.querySelector(ops.target) : this.$el;
    },

    init() {
      // Set up the responsive breakpoint handler so the slider can be
      // initialised/destroyed as the viewport crosses the breakpoint.
      if (ops.breakpoint) {
        let resizeTimer = null;
        this.resizeHandler = () => {
          // Debounce so we don't thrash during a resize drag.
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.handleBreakpointChange(), 150);
        };
        window.addEventListener("resize", this.resizeHandler);
      }

      this.handleBreakpointChange();
    },

    destroy() {
      if (this.resizeHandler) {
        window.removeEventListener("resize", this.resizeHandler);
        this.resizeHandler = null;
      }
      this.destroySwiper();
    },

    handleBreakpointChange() {
      const shouldDisable = this.shouldDisableAtBreakpoint();

      if (shouldDisable) {
        if (this.swiper) {
          this.destroySwiper();
        }
        this.disabled = true;
        return;
      }

      this.disabled = false;
      if (!this.swiper) {
        this.initSwiper();
      }
    },

    initSwiper() {
      const el = this.getTargetEl();

      if (!el) {
        console.warn('Slider: target element not found');
        this.disabled = true;
        return;
      }

      // If breakpoint config exists, dynamically add swiper classes
      if (ops.breakpoint) {
        this.applySwiperClasses(el);
      }

      const swiperOps = { ...ops };
      if (prefersReducedMotion() && swiperOps.autoplay) {
        delete swiperOps.autoplay;
      }

      this.config = Object.assign(
        {
          slidesPerView: "auto",
          createElements: false,
          touchEventsTarget: 'container',
        },
        swiperOps,
      );

      this.swiper = new Swiper(el, this.config);

      this.swiper.on("slideChange", () => {
        this.$nextTick(() => {
          this.current = this.swiper.realIndex;
        });
      });

      this.swiper.on("progress", (swiper, progress) => {
        this.$nextTick(() => {
          this.scrollProgress = progress;
        });
      });

      this.scrollProgress = this.swiper.progress;


      this.swiper.on("autoplayPause", () => {
        this.$nextTick(() => {
          this.isPaused = true;
        });
      });

      this.swiper.on("autoplayResume", () => {
        this.$nextTick(() => {
          this.isPaused = false;
        });
      });

      this.swiper.on("autoplayStart", () => {
        this.$nextTick(() => {
          this.isStopped = false;
        });
      });

      this.swiper.on("autoplayStop", () => {
        this.$nextTick(() => {
          this.isStopped = true;
        });
      });

      // Stop timer going wonky on document blur/focus
      if (!this.visibilityHandler) {
        this.visibilityHandler = () => {
          if (!document.hidden) {
            this.progress = 0;
            this.progressPercentage = 0;
          }
        };
        document.addEventListener("visibilitychange", this.visibilityHandler);
      }

      /**
       * Accessibility tweaks and fixes
       */
      // add intersection observer to add/remove aria-hidden="true" if slide is not visible
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const links = entry.target.querySelectorAll("a");

            // entry is visible
            if (entry.intersectionRatio > 0) {
              entry.target.setAttribute("aria-hidden", "false");
              links.forEach((link) => {
                link.removeAttribute("tabindex");
              });
              return;
            }

            // entry is not visible
            entry.target.setAttribute("aria-hidden", "true");
            links.forEach((link) => {
              link.setAttribute("tabindex", "-1");
            });
          });
        },
        { root: el },
      );
      el.querySelectorAll(".swiper-slide").forEach((t) => this.observer.observe(t));

      this.animation.fpsInterval = 1000 / this.animation.fps;
      this.animation.then = Date.now();
      this.animation.startTime = this.animation.then;

      this.rafId = requestAnimationFrame(this.updateProgress.bind(this));
    },

    destroySwiper() {
      // Cancel the progress RAF loop.
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      // Disconnect the a11y intersection observer.
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Tear down the visibility listener.
      if (this.visibilityHandler) {
        document.removeEventListener("visibilitychange", this.visibilityHandler);
        this.visibilityHandler = null;
      }

      const el = this.getTargetEl();

      // Destroy Swiper, clean inline styles, and strip the helper classes
      // so the markup reverts to its native, non-swiper layout.
      if (this.swiper) {
        try {
          this.swiper.destroy(true, true);
        } catch (e) {
          // no-op
        }
        this.swiper = null;
      }

      if (ops.breakpoint) {
        this.removeSwiperClasses(el);
      }

      // Reset a11y attributes that were toggled while swiper was active.
      if (el) {
        el.querySelectorAll('[data-swiper-slide]').forEach((slide) => {
          slide.removeAttribute("aria-hidden");
          slide.querySelectorAll("a").forEach((link) => link.removeAttribute("tabindex"));
        });
      }

      // Reset state.
      this.current = 0;
      this.isPaused = false;
      this.isStopped = false;
      this.lastIndex = -1;
      this.progress = 0;
      this.progressPercentage = 0;
      this.scrollProgress = 0;
    },

    appendSlides(slides) {
      if (!this.swiper) return;
      this.$nextTick(() => {
        this.swiper.appendSlide(slides);
      });
    },

    goTo(idx) {
      if (!this.swiper) return;
      this.swiper.slideTo(idx);
    },

    stopAutoplay() {
      if (!this.swiper) return;

      const { swiper } = this;

      if (swiper.autoplay?.running) {
        swiper.autoplay.stop();
      }

      const currentTranslate = swiper.getTranslate();
      swiper.setTransition(0);
      swiper.setTranslate(currentTranslate);
      swiper.animating = false;

      this.isPaused = true;
      this.isStopped = true;
      this.progress = 0;
      this.progressPercentage = 0;
    },

    next() {
      if (!this.swiper) return;
      this.stopAutoplay();
      this.swiper.slideNext(300);
    },

    prev() {
      if (!this.swiper) return;
      this.stopAutoplay();
      this.swiper.slidePrev(300);
    },

    updateProgress() {
      const self = this;

      // Exit early if swiper has been destroyed; don't re-queue the loop.
      if (!self.swiper) {
        self.rafId = null;
        return;
      }

      self.rafId = requestAnimationFrame(self.updateProgress.bind(this));

      // calc elapsed time since last loop

      this.animation.now = Date.now();
      this.animation.elapsed = this.animation.now - this.animation.then;

      // if enough time has elapsed, draw the next frame

      if (this.animation.elapsed > this.animation.fpsInterval) {
        this.animation.then =
          this.animation.now -
          (this.animation.elapsed % this.animation.fpsInterval);

        const isStopped = self.isStopped;
        const isPaused = self.isPaused;
        const activeIndex = self.swiper.activeIndex;
        const speed = self.swiper.params.speed;
        const delay = self.swiper.params.autoplay.delay;

        if (!isPaused) {
          if (!isStopped) {
            if (self.lastIndex === self.current - 1) {
              self.progress++;
            } else {
              self.lastIndex = self.current - 1;
              self.progress = 0;
            }

            if (activeIndex > 1) {
              self.progressPercentage =
                (self.progress / 60 / ((delay + speed) / 1000)) * 100;
            } else {
              self.progressPercentage =
                (self.progress / 60 / (delay / 1000)) * 100;
            }
          } else {
            self.lastIndex = self.current - 1;
            self.progress = 0;
            self.progressPercentage = 0;
          }
        } else {
          if (self.progress > 0) {
            self.progress = 0;
            self.progressPercentage =
              (self.progress / 60 / (delay / 1000)) * 100;
          }
        }
      }
    },
  };
}
